import { i18n } from '@lingui/core';

type MoneyAmount = {
  amount: number;
};

export type PriceListDiscount = {
  priceListName?: string | null;
  discountPercent?: number | null;
  /** Catalog deal already shown on the banner — omit the matching name on the line. */
  contextPriceListName?: string | null;
};

export type PriceListDiscountLabelTexts = {
  withName: (priceListName: string, percentText: string) => string;
  percentOnly: (percentText: string) => string;
};

export type PriceListContextBannerTexts = {
  withPercent: (percentText: string) => string;
  agreementOnly: string;
  activeLabel: string;
};

export const defaultPriceListDiscountLabelTexts: PriceListDiscountLabelTexts = {
  withName: (priceListName, percentText) => `${priceListName}, ${percentText}% off`,
  percentOnly: (percentText) => `${percentText}% off`
};

export const defaultPriceListContextBannerTexts: PriceListContextBannerTexts = {
  withPercent: (percentText) => `Prices include a ${percentText}% discount.`,
  agreementOnly: 'Prices follow this customer agreement.',
  activeLabel: 'Active'
};

export function hasDiscountPercent(value: number | null | undefined): value is number {
  return value != null && value > 0;
}

function discountPercentLocale(locale: string): string {
  switch (locale) {
    case 'se':
      return 'sv-SE';
    case 'nb':
      return 'nb-NO';
    default:
      return 'en-US';
  }
}

export function formatDiscountPercent(value: number, locale = i18n.locale): string {
  return new Intl.NumberFormat(discountPercentLocale(locale), {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
    useGrouping: false
  }).format(Number(value.toFixed(2)));
}

export function formatPriceListDiscountLabel({
  priceListName,
  discountPercent,
  contextPriceListName,
  texts = defaultPriceListDiscountLabelTexts
}: PriceListDiscount & { texts?: PriceListDiscountLabelTexts }): string | null {
  const percentText = hasDiscountPercent(discountPercent) ? formatDiscountPercent(discountPercent) : null;
  const showName = Boolean(priceListName) && priceListName !== contextPriceListName;

  if (percentText && showName && priceListName) {
    return texts.withName(priceListName, percentText);
  }
  if (percentText) {
    return texts.percentOnly(percentText);
  }
  return showName ? (priceListName ?? null) : null;
}

/** List price to strike through when a customer context discount lowered the payable price. */
export function getStruckThroughCatalogPrice<T extends MoneyAmount>({
  payable,
  original
}: {
  payable: T;
  original?: T | null;
}): T | null {
  if (!original || original.amount <= payable.amount) {
    return null;
  }
  return original;
}
